import React from 'react'
import _ from 'lodash'
import { useSelector, useDispatch } from 'react-redux'
import { Chip, TextField, Typography, Box, CircularProgress } from '@mui/material'
import { saveTemporalFeatures } from '../../../redux/slices/Tree/treeFeatures'
import * as service from '../../../services'

const KEY_OTHERS_IN_CATEGORY_TYPE = [86, 87, 88, 89, 90, 91, 92]

const InconvenienceAutoSelect = ({ categoryId, label, editable, error, required = false }) => {
  const dispatch = useDispatch()
  const [options, setOptions] = React.useState(null)
  const [otherText, setOtherText] = React.useState('')
  const temporalFeature = useSelector(state => state.treeFeatures.temporal)

  const selectedItems = React.useMemo(
    () => _.filter(temporalFeature, ['category', categoryId]),
    [temporalFeature, categoryId],
  )

  const selectedSet = React.useMemo(
    () => new Set(selectedItems.map(item => item.typecategory)),
    [selectedItems],
  )

  const otherSelected = React.useMemo(
    () => selectedItems.find(item => KEY_OTHERS_IN_CATEGORY_TYPE.includes(item.typecategory)),
    [selectedItems],
  )

  React.useEffect(() => {
    if (otherSelected) setOtherText(otherSelected.initvalue || '')
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    service
      .getCTByCategory(categoryId)
      .then(response => setOptions(response))
      .catch(err => console.log('Error:', err.message))
  }, [categoryId])

  const handleToggle = React.useCallback(
    option => {
      if (!editable) return
      const isSelected = selectedSet.has(option.typecategory)
      const otherCategories = temporalFeature.filter(item => item.category !== categoryId)

      if (isSelected) {
        const remaining = selectedItems.filter(item => item.typecategory !== option.typecategory)
        dispatch(saveTemporalFeatures([...otherCategories, ...remaining]))
      } else {
        dispatch(saveTemporalFeatures([...otherCategories, ...selectedItems, option]))
      }
    },
    [temporalFeature, categoryId, selectedItems, selectedSet, editable, dispatch],
  )

  const handleOtherTextChange = React.useCallback(
    e => {
      const text = e.target.value.slice(0, 75)
      setOtherText(text)
      if (!otherSelected) return
      const updated = temporalFeature.map(item =>
        item.typecategory === otherSelected.typecategory ? { ...item, initvalue: text } : item,
      )
      dispatch(saveTemporalFeatures(updated))
    },
    [temporalFeature, otherSelected, dispatch],
  )

  if (!options) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">
          Cargando {label}...
        </Typography>
      </Box>
    )
  }

  const isOtherOption = option => KEY_OTHERS_IN_CATEGORY_TYPE.includes(option.typecategory)
  const regularOptions = options.filter(o => !isOtherOption(o))
  const otherOption = options.find(o => isOtherOption(o))

  return (
    <Box>
      <Typography
        variant="body2"
        fontWeight={600}
        color={error ? 'error' : 'text.secondary'}
        sx={{ mb: 0.75 }}
      >
        {label}{required ? ' *' : ''}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {regularOptions.map(option => {
          const isSelected = selectedSet.has(option.typecategory)
          return (
            <Chip
              key={option.typecategory}
              label={option.label}
              size="medium"
              variant={isSelected ? 'filled' : 'outlined'}
              color={isSelected ? 'primary' : 'default'}
              onClick={() => handleToggle(option)}
              sx={{
                height: 36,
                fontSize: '0.84rem',
                fontWeight: isSelected ? 600 : 400,
                transition: 'all 0.15s',
                cursor: editable ? 'pointer' : 'default',
                '&:active': editable ? { transform: 'scale(0.95)' } : {},
              }}
            />
          )
        })}
        {otherOption && (
          <Chip
            label={otherOption.label}
            size="medium"
            variant={selectedSet.has(otherOption.typecategory) ? 'filled' : 'outlined'}
            color={selectedSet.has(otherOption.typecategory) ? 'secondary' : 'default'}
            onClick={() => handleToggle(otherOption)}
            sx={{
              height: 36,
              fontSize: '0.84rem',
              fontWeight: selectedSet.has(otherOption.typecategory) ? 600 : 400,
              transition: 'all 0.15s',
              cursor: editable ? 'pointer' : 'default',
            }}
          />
        )}
      </Box>
      {otherSelected && (
        <TextField
          value={otherText}
          onChange={handleOtherTextChange}
          label="Especificar"
          variant="standard"
          size="small"
          fullWidth
          inputProps={{ maxLength: 75 }}
          error={!otherText.trim()}
          helperText={!otherText.trim() ? 'Obligatorio cuando se selecciona "Otros"' : 'Máximo 75 caracteres'}
          sx={{ mt: 1 }}
          InputProps={{ readOnly: !editable }}
        />
      )}
      {error && selectedItems.length === 0 && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          Seleccione al menos una opción
        </Typography>
      )}
    </Box>
  )
}

export default React.memo(InconvenienceAutoSelect)
